import { useState } from 'react';
import { diffLines } from 'diff';
import type { AuditChanges as Changes } from '@fenix/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FIELD_LABEL, formatValue } from './labels';

const MAX_INLINE = 80;

function Value({ field, value, className }: { field: string; value: unknown; className: string }) {
  const text = formatValue(field, value);
  const long = text.length > MAX_INLINE;
  return (
    <span className={`rounded px-1.5 py-0.5 ${className}`} title={long ? text : undefined}>
      {long ? `${text.slice(0, MAX_INLINE)}…` : text}
    </span>
  );
}

/**
 * Texto longo (script, descrição de marca) não cabe numa célula, e lado a lado também não
 * responde "o que mudou". A janela mostra o diff linha a linha: removidas em vermelho,
 * adicionadas em verde, e trechos iguais recolhidos para sobrar só o contexto.
 */
const CONTEXT_LINES = 2;

function LineDiff({ from, to }: { from: string; to: string }) {
  const parts = diffLines(from, to);
  const added = parts.filter((p) => p.added).reduce((n, p) => n + p.count!, 0);
  const removed = parts.filter((p) => p.removed).reduce((n, p) => n + p.count!, 0);
  if (!added && !removed) {
    return <p className="text-sm text-muted-foreground">Os dois textos são iguais.</p>;
  }
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        <span className="text-green-700 dark:text-green-300">+{added}</span>
        {' / '}
        <span className="text-destructive">−{removed}</span> linhas
      </p>
      <pre className="min-h-0 flex-1 overflow-auto rounded-md border font-mono text-xs leading-5">
        {parts.map((part, i) => {
          const lines = part.value.replace(/\n$/, '').split('\n');
          if (part.added || part.removed) {
            return lines.map((line, j) => (
              <div
                key={`${i}-${j}`}
                className={
                  part.added
                    ? 'whitespace-pre-wrap break-words bg-success/15 px-3 text-green-800 dark:text-green-200'
                    : 'whitespace-pre-wrap break-words bg-destructive/10 px-3 text-destructive'
                }
              >
                {part.added ? '+ ' : '− '}
                {line}
              </div>
            ));
          }
          const first = i === 0;
          const last = i === parts.length - 1;
          const keepHead = first ? 0 : CONTEXT_LINES;
          const keepTail = last ? 0 : CONTEXT_LINES;
          const hidden = lines.length - keepHead - keepTail;
          if (hidden <= 1) {
            return lines.map((line, j) => (
              <div
                key={`${i}-${j}`}
                className="whitespace-pre-wrap break-words px-3 text-muted-foreground"
              >
                {'  '}
                {line}
              </div>
            ));
          }
          return [
            ...lines.slice(0, keepHead).map((line, j) => (
              <div
                key={`${i}-h${j}`}
                className="whitespace-pre-wrap break-words px-3 text-muted-foreground"
              >
                {'  '}
                {line}
              </div>
            )),
            <div
              key={`${i}-skip`}
              className="bg-muted/50 px-3 py-0.5 text-center text-muted-foreground"
            >
              ⋯ {hidden} linhas iguais ⋯
            </div>,
            ...lines.slice(lines.length - keepTail).map((line, j) => (
              <div
                key={`${i}-t${j}`}
                className="whitespace-pre-wrap break-words px-3 text-muted-foreground"
              >
                {'  '}
                {line}
              </div>
            )),
          ];
        })}
      </pre>
    </div>
  );
}

function LongChange({ label, from, to }: { label: string; from: string; to: string }) {
  const [open, setOpen] = useState(false);
  const preview = (s: string) => (s.length > MAX_INLINE ? `${s.slice(0, MAX_INLINE)}…` : s);
  return (
    <>
      <span className="rounded bg-muted px-1.5 py-0.5">{preview(to)}</span>
      <button
        type="button"
        className="ml-1 text-primary underline-offset-2 hover:underline"
        onClick={() => setOpen(true)}
      >
        ver alteração
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              Linhas removidas em vermelho, adicionadas em verde; o que não mudou fica recolhido.
            </DialogDescription>
          </DialogHeader>
          <LineDiff from={from} to={to} />
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Diff colorido: verde = criado/novo, riscado vermelho = removido/antigo. Listas viram +/−. */
export function AuditChanges({ changes }: { changes: Changes | null | undefined }) {
  const entries = Object.entries(changes ?? {});
  if (!entries.length) return <span className="text-muted-foreground">—</span>;
  return (
    <ul className="space-y-1">
      {entries.map(([field, { from, to }]) => {
        const label = FIELD_LABEL[field] ?? field;
        if (Array.isArray(from) || Array.isArray(to)) {
          const a = new Set((from as unknown[]) ?? []);
          const b = new Set((to as unknown[]) ?? []);
          const added = [...b].filter((x) => !a.has(x));
          const removed = [...a].filter((x) => !b.has(x));
          return (
            <li key={field} className="text-xs">
              <span className="text-muted-foreground">{label}: </span>
              {added.map((x) => (
                <span
                  key={`a-${String(x)}`}
                  className="mr-1 rounded bg-success/15 px-1.5 py-0.5 text-green-700 dark:text-green-300"
                >
                  + {String(x)}
                </span>
              ))}
              {removed.map((x) => (
                <span
                  key={`r-${String(x)}`}
                  className="mr-1 rounded bg-destructive/10 px-1.5 py-0.5 text-destructive"
                >
                  − {String(x)}
                </span>
              ))}
              {!added.length && !removed.length && (
                <span className="text-muted-foreground">sem mudança</span>
              )}
            </li>
          );
        }
        const created = from === null || from === undefined;
        const deleted = to === null || to === undefined;
        const fromText = formatValue(field, from);
        const toText = formatValue(field, to);
        const long =
          !created && !deleted && (fromText.length > MAX_INLINE || toText.length > MAX_INLINE);
        return (
          <li key={field} className="text-xs">
            <span className="text-muted-foreground">{label}: </span>
            {long ? (
              <LongChange label={label} from={fromText} to={toText} />
            ) : created ? (
              <Value
                field={field}
                value={to}
                className="bg-success/15 text-green-700 dark:text-green-300"
              />
            ) : deleted ? (
              <Value
                field={field}
                value={from}
                className="bg-destructive/10 text-destructive line-through"
              />
            ) : (
              <>
                <Value field={field} value={from} className="text-destructive/80 line-through" />
                <span className="mx-1 text-muted-foreground">→</span>
                <Value
                  field={field}
                  value={to}
                  className="bg-success/15 text-green-700 dark:text-green-300"
                />
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
